// `GET /api/sports/:sport/calendar?season=&after=` — a sport's season as a new
// league would meet it. PLAN-2026-09-24 item A.
//
// WHAT IT IS FOR. A league made after its sport's season has started plays
// the weeks still to come, and which weeks those are depends on two facts the
// browser does not have: the real pro schedule, and the instant the league
// drafts. So the server answers, per period, "can a league drafting then start
// its season here" — and `POST /api/leagues` refuses anything this says is not.
// A client that has never seen this repo offers `startable` periods, suggests
// `suggestedFirst`, and needs no rule of its own.
//
// PUBLIC. It names no league and nobody; it is the pro schedule with one
// question asked of it.

import { z } from 'zod';

export const SeasonPeriodOption = z.object({
  /** The period index a league's settings name (`schedule.regularSeason.firstPeriod`). */
  index: z.number().int().min(1),
  /** The sport's own word for it: "Week 3". */
  label: z.string(),
  start: z.string(),
  end: z.string(),
  /** The period's first real kickoff; null for a period with no games. */
  firstKickoff: z.string().nullable(),
  /** Where it falls in the sport's default season: a new league's shape. */
  kind: z.enum(['regular', 'playoff', 'off']),
  /** A regular period whose first kickoff is after `after`. */
  startable: z.boolean(),
});
export type SeasonPeriodOption = z.infer<typeof SeasonPeriodOption>;

export const SeasonCalendarView = z.object({
  sport: z.string(),
  season: z.number().int(),
  /** The instant `startable` was judged at: the draft time asked about, or now. */
  after: z.string(),
  /** The sport's default regular season, by period index. */
  regularSeason: z.object({ first: z.number().int(), last: z.number().int() }),
  /** The sport's default playoff periods. */
  playoffs: z.array(z.number().int()),
  /** Every period the schedule has, in order. Empty before the schedule is loaded. */
  periods: z.array(SeasonPeriodOption),
  /**
   * The first startable period — what `POST /api/leagues` uses when it is sent
   * no `firstPeriod`. Null when no regular period is left to start, or the
   * schedule is not loaded yet.
   */
  suggestedFirst: z.number().int().nullable(),
  asOf: z.string(),
});
export type SeasonCalendarView = z.infer<typeof SeasonCalendarView>;

/** The query. `after` defaults to now. */
export const SeasonCalendarQuery = z.object({
  season: z.coerce.number().int().min(1999).max(2100),
  after: z.string().datetime({ offset: true }).optional(),
});
export type SeasonCalendarQuery = z.infer<typeof SeasonCalendarQuery>;
